import React from 'react';

import { Checkbox } from '@atlaskit/checkbox';

import {
  Table,
  createColumnWidths,
  createColumnsWidths,
  createPredefinedColumnWidths,
} from '@qality/common-components/src/atoms/Table';
import { useTestCycleSorterContext } from '@qality/common-components/src/test-cycle/TestCycleSorter';
import { StorageOfUniqueServices } from '@qality/common-service';

import { useTestCasesInCycleList } from '../TestCaseInCycleListProvider';
import {
  ExecutionAssigneeSortingStrategy,
  NumberOfBugsSortingStrategy,
  StatusSortingStrategy,
  SummarySortingStrategy,
  TypeSortingStrategy,
} from './TestCasesInCycleSortableTableHeader.const';

type Columns =
  | 'dnd'
  | 'checkbox'
  | 'testType'
  | 'testCase'
  | 'status'
  | 'executionAssignee'
  | 'bugs'
  | 'actions';

const ColumnsWidths = createColumnsWidths<Columns>({
  dnd: createPredefinedColumnWidths('dnd'),
  checkbox: createPredefinedColumnWidths('checkbox'),
  testType: createColumnWidths('48px', '48px'),
  testCase: createColumnWidths('auto', '240px'),
  status: createColumnWidths('154px', '154px', '154px'),
  executionAssignee: createColumnWidths('240px', '240px', '240px'),
  bugs: createColumnWidths('auto'),
  actions: createColumnWidths('194px', '194px', '194px'),
});

export const NUMBER_OF_COLUMNS_PREMIUM = Object.keys(ColumnsWidths).length;

type Props = {
  isAllCurentlyDisplayedTestCasesEmpty: boolean;
  isAllTestCasesSelected: boolean;
  onAllTestCasesSelection: () => void;
};

export function TestCasesInCycleSortableTableHeader({
  isAllCurentlyDisplayedTestCasesEmpty,
  isAllTestCasesSelected,
  onAllTestCasesSelection,
}: Props): JSX.Element {
  const { testCycle } = useTestCasesInCycleList();

  const { testCycleSortStrategy, setTestCycleSortStrategy } =
    useTestCycleSorterContext();

  const IS_ADVANCED = StorageOfUniqueServices.get('LicenseService').isAdvanced;

  return (
    <Table.Head>
      <Table.Tr>
        <Table.DndTh widths={ColumnsWidths.dnd} aria-label='Drag and drop' />
        <Table.CheckboxTh
          widths={ColumnsWidths.checkbox}
          aria-label='Bulk select'
        >
          <Table.ContentHelper height='compact' alignItems='center'>
            <Checkbox
              isDisabled={isAllCurentlyDisplayedTestCasesEmpty}
              isChecked={isAllTestCasesSelected}
              onChange={onAllTestCasesSelection}
            />
          </Table.ContentHelper>
        </Table.CheckboxTh>
        {IS_ADVANCED ? (
          <Table.SortableTh
            sortStrategy={testCycleSortStrategy}
            sortStrategies={TypeSortingStrategy}
            onSortStrategyChange={setTestCycleSortStrategy}
            widths={ColumnsWidths.testType}
          >
            Type
          </Table.SortableTh>
        ) : (
          <Table.Th widths={ColumnsWidths.testType}>Type</Table.Th>
        )}

        <Table.SortableTh
          sortStrategy={testCycleSortStrategy}
          sortStrategies={SummarySortingStrategy}
          onSortStrategyChange={setTestCycleSortStrategy}
          widths={ColumnsWidths.testCase}
        >
          Test Case
        </Table.SortableTh>
        <Table.SortableTh
          sortStrategy={testCycleSortStrategy}
          sortStrategies={StatusSortingStrategy}
          onSortStrategyChange={setTestCycleSortStrategy}
          widths={ColumnsWidths.status}
        >
          Status
        </Table.SortableTh>
        <Table.SortableTh
          sortStrategy={testCycleSortStrategy}
          sortStrategies={ExecutionAssigneeSortingStrategy}
          onSortStrategyChange={setTestCycleSortStrategy}
          widths={ColumnsWidths.executionAssignee}
        >
          Execution Assignee
        </Table.SortableTh>
        <Table.SortableTh
          sortStrategy={testCycleSortStrategy}
          sortStrategies={NumberOfBugsSortingStrategy}
          onSortStrategyChange={setTestCycleSortStrategy}
          widths={ColumnsWidths.bugs}
        >
          Bugs
        </Table.SortableTh>
        {testCycle.canEdit && (
          <Table.Th widths={ColumnsWidths.actions}>Actions</Table.Th>
        )}
      </Table.Tr>
    </Table.Head>
  );
}
